import React, { Component, ErrorInfo, FC, ReactNode, useEffect, useMemo } from 'react';
import { SpanStatusCode, trace } from '@opentelemetry/api';
import { OTELContext } from './ctx';
import { OTELContextProps } from '../../models/context/otel';
import { tracer } from '@domgen/shared/services/otel/tracer';
import { enhanceSpan } from '../../utils/otel/enhanceTrace';
import { log } from '@domgen/shared/utils/log';

// ---------------------------------------------------------------------------
// React Error Boundary — captures render-time errors as OTEL span events
// ---------------------------------------------------------------------------

type ErrorBoundaryProps = { children: ReactNode; namespace: string };
type ErrorBoundaryState = { hasError: boolean };

class OTELErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const span = trace.getActiveSpan();
    if (span) {
      span.recordException(error);
      span.setAttribute('exception.message', error.message);
      span.setAttribute('exception.name', error.name);
      if (info.componentStack) {
        span.setAttribute('exception.componentStack', info.componentStack);
      }
      span.setStatus({ code: SpanStatusCode.ERROR });
    }

    enhanceSpan(this.props.namespace, { event: 'reactRenderError', message: error.message }, true);
    log.error({ message: 'React render error captured by OTELErrorBoundary', data: error });
  }

  render(): ReactNode {
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * `OTELProvider` wraps your application and takes care of three concerns:
 *
 * 1. **Tracer initialisation** – calls `tracer()` on first mount using the
 *    config values passed as `value` props. No need to call `tracer()` from
 *    `createApp` any more.
 *
 * 2. **Global error detection** – registers `window` listeners for unhandled
 *    runtime errors and unhandled promise rejections and records them on the
 *    active OTEL span.
 *
 * 3. **React render-error capture** – wraps `children` in an `ErrorBoundary`
 *    that records React render failures as span exceptions.
 *
 * Use `useOTEL()` inside the tree to start spans, add events, and record
 * errors from your own components.
 */
export const OTELProvider: FC<{
  value: Partial<OTELContextProps>;
  children: ReactNode;
}> = ({ value, children }) => {
  const namespace = value.namespace ?? 'olr';

  const contextValue = useMemo<Partial<OTELContextProps>>(
    () => ({
      ...value,
      namespace,
      hostName: value.hostName ?? globalThis.location?.hostname,
      apiBaseUrl: value.apiBaseUrl ?? import.meta.env.VITE_BASE_API_URL,
    }),
    [value],
  );

  // Initialise the OTEL SDK once on mount using the config props.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    tracer({
      version: value.version,
      namespace,
      brandCode: value.brandCode,
      otelExporterEndpoint: value.otelExporterEndpoint,
      otelToken: value.otelToken,
      environment: value.environment,
      traceParentCorsUrls: value.apiBaseUrl ? [new RegExp(value.apiBaseUrl)] : [],
    }).catch((err: unknown) => {
      log.warn({ message: '[OTELProvider] Failed to initialize tracer', data: err });
    });

    enhanceSpan(namespace, { ['session.id']: value.sessionId ?? 'unknown' });
    // Run only once — config values are treated as stable at mount time.
  }, []);

  // Register global error listeners and clean up when the provider unmounts.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleError = (event: ErrorEvent): void => {
      const message = event.error instanceof Error ? event.error.message : event.message;
      enhanceSpan(namespace, { event: 'windowError', message }, true);
      log.error({ message: 'Unhandled runtime error', data: event.error ?? event.message });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
      const reason =
        event.reason instanceof Error ? event.reason.message : JSON.stringify(event.reason);
      enhanceSpan(namespace, { event: 'unhandledRejection', reason }, true);
      log.error({ message: 'Unhandled promise rejection', data: event.reason });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [namespace]);

  return (
    <OTELContext.Provider value={contextValue}>
      <OTELErrorBoundary namespace={namespace}>{children}</OTELErrorBoundary>
    </OTELContext.Provider>
  );
};
